import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import TopBar from "@/components/TopBar";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import BackgroundMedia from "@/components/BackgroundMedia";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { Container, Reveal } from "@/components/primitives";
import { api, mediaSrc } from "@/lib/api";

const FB = {
  HERO: "https://images.unsplash.com/photo-1724775255163-b0f5f5241642?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  COLLAB: "https://images.unsplash.com/photo-1598149255714-de0a630b96ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
  TEXTURE: "https://images.unsplash.com/photo-1771797573645-7483eab8207f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
  SHADOW: "https://images.unsplash.com/photo-1772442363851-738a548f6c5c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100",
  CLOSING: "https://images.unsplash.com/photo-1772442198907-765685249ea4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};
const FB_LANGUAGE = [
  { head: "Designed Around YÜ", body: "Furniture tailored to the rhythm and character of a space." },
  { head: "Curated for YÜ", body: "Objects and pieces selected intentionally." },
  { head: "Built with YÜ", body: "A collaborative process between vision and execution." },
  { head: "Spaces Completed for YÜ", body: "Final-layer refinement and styling." },
];

export default function UnderstandingYu() {
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.getPage("understanding_yu").then(setDoc).catch(() => {}); }, []);
  const c = doc?.content || {};
  const hero = c.hero || {}, whatIs = c.what_is || {}, philosophy = c.philosophy || {}, language = c.language || {}, closing = c.closing || {};

  const heroImg = hero.image_url ? mediaSrc(hero.image_url) : FB.HERO;
  const heroEyebrow = hero.eyebrow || "· A PHILOSOPHY ·";
  const heroHead = hero.headline || "YÜ is not\none person.";
  const heroSub = hero.sub || "YÜ represents everyone connected to the creation of a meaningful space.";

  const whatLabel = whatIs.label || "02 · WHAT IS YÜ";
  const whatHeading = whatIs.heading || "Understanding YÜ";
  const whatOpening = whatIs.opening || "YÜ represents the people a space is ultimately created for.";
  const triplet = Array.isArray(whatIs.triplet) && whatIs.triplet.length ? whatIs.triplet : ["The architect shaping the vision.", "The designer refining the experience.", "The client living within it."];
  const whatClosing = whatIs.closing || "Every detail begins by understanding YÜ — the rhythm, personality and intent behind the space.";
  const whatImg = whatIs.image_url ? mediaSrc(whatIs.image_url) : FB.COLLAB;
  const whatCaption = whatIs.image_caption || "Collaboration · Sketch · Material.";

  const philLabel = philosophy.label || "03 · PHILOSOPHY";
  const philHeading = philosophy.heading || "Designed\nAround YÜ.";
  const philImgA = philosophy.image_a_url ? mediaSrc(philosophy.image_a_url) : FB.TEXTURE;
  const philBodyA = philosophy.body_a || "Great spaces are not built around products. They are built around people, emotion, rhythm and belonging.";
  const philImgB = philosophy.image_b_url ? mediaSrc(philosophy.image_b_url) : FB.SHADOW;
  const philBodyB = philosophy.body_b || "YÜ reflects individuality, collaboration and the relationship between people and the spaces they inhabit.";
  const philBodyC = philosophy.body_c || "Everything we create begins with understanding that connection.";

  const langLabel = language.label || "04 · LANGUAGE";
  const langHeading = language.heading || "How YÜ lives\nin our language.";
  const langItems = Array.isArray(language.items) && language.items.length ? language.items : FB_LANGUAGE;

  const closingImg = closing.image_url ? mediaSrc(closing.image_url) : FB.CLOSING;
  const closingEyebrow = closing.eyebrow || "· A FINAL THOUGHT ·";
  const closingHead = closing.headline || "Because great spaces are never designed for everyone.";
  const closingFinal = closing.final_line || "They are designed for YÜ.";
  const ml = (t) => t.split("\n").map((line, i) => <span key={i} className="block">{line}</span>);

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo title="Understanding YÜ · Onedot6" description={heroSub} path="/understanding-yu" />
      <TopBar />
      {/* HERO */}
      <section className="relative min-h-[720px] md:min-h-[880px] flex flex-col justify-end overflow-hidden px-6 md:px-12 pt-40 pb-24 md:pb-32">
        <BackgroundMedia videoUrl={hero.video_url} imageUrl={heroImg} testId="yu-hero-media" />
        <div className="absolute inset-0 bg-gradient-to-b from-lux-ink/55 via-lux-ink/35 to-lux-ink/85 pointer-events-none" />
        <div className="relative w-full max-w-[1180px] mx-auto">
          <p className="text-[11px] tracking-[0.35em] text-lux-accent mb-4 font-medium">{heroEyebrow}</p>
          <h1 className="font-serif text-white font-light tracking-tight leading-[0.95] text-[clamp(3.5rem,9vw,7.5rem)]">{ml(heroHead)}</h1>
          <p className="text-white/80 font-light leading-relaxed mt-6 text-base md:text-lg max-w-[640px]">{heroSub}</p>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 animate-bounce">
          <span className="text-[10px] tracking-[0.35em] text-white/70">SCROLL</span>
          <ChevronDown size={18} className="text-white/70" />
        </div>
      </section>

      {/* WHAT IS YÜ */}
      <Container className="pt-24 max-w-[1180px]">
        <p className="text-[11px] tracking-[0.35em] text-lux-accent mb-3.5 font-medium">{whatLabel}</p>
        <h2 className="font-serif text-4xl md:text-5xl text-lux-ink font-light tracking-tight leading-tight">{whatHeading}</h2>
        <p className="font-serif italic text-lux-ink font-light leading-relaxed mt-7 text-xl md:text-2xl">{whatOpening}</p>
        <div className="flex flex-col gap-3.5 py-6">
          {triplet.map((t) => (
            <div key={t} className="flex items-center gap-3.5"><span className="w-6 h-px bg-lux-accent" /><span className="text-base text-lux-sec font-light leading-snug">{t}</span></div>
          ))}
        </div>
        <p className="font-serif italic text-lux-ink font-light leading-relaxed text-xl md:text-2xl">{whatClosing}</p>
        <Reveal><img src={whatImg} alt="" className="w-full h-[400px] object-cover bg-lux-line mt-9" /></Reveal>
        <p className="text-[11px] tracking-[0.25em] uppercase text-lux-muted mt-3">{whatCaption}</p>
      </Container>

      {/* PHILOSOPHY */}
      <Container className="pt-24 max-w-[1180px]">
        <p className="text-[11px] tracking-[0.35em] text-lux-accent mb-3.5 font-medium">{philLabel}</p>
        <h2 className="font-serif text-4xl md:text-5xl text-lux-ink font-light tracking-tight leading-tight">{ml(philHeading)}</h2>
        <Reveal><img src={philImgA} alt="" className="w-full h-[400px] object-cover bg-lux-line mt-8" /></Reveal>
        <p className="font-serif text-lux-ink font-light leading-relaxed mt-7 text-xl md:text-2xl">{philBodyA}</p>
        <Reveal><img src={philImgB} alt="" className="w-full h-[400px] object-cover bg-lux-line mt-6" /></Reveal>
        <p className="font-serif text-lux-ink font-light leading-relaxed mt-7 text-xl md:text-2xl">{philBodyB}</p>
        <p className="font-serif text-lux-ink font-light leading-relaxed mt-4.5 mt-5 text-xl md:text-2xl">{philBodyC}</p>
      </Container>

      {/* LANGUAGE */}
      <Container className="pt-24 max-w-[1180px]">
        <p className="text-[11px] tracking-[0.35em] text-lux-accent mb-3.5 font-medium">{langLabel}</p>
        <h2 className="font-serif text-4xl md:text-5xl text-lux-ink font-light tracking-tight leading-tight">{ml(langHeading)}</h2>
        <div className="mt-10">
          {langItems.map((l, i) => (
            <div key={l.head || i} className="py-7 border-t border-lux-line" data-testid={`language-${i}`}>
              <p className="text-[11px] tracking-[0.25em] text-lux-accent mb-2.5">{`0${i + 1}`}</p>
              <h3 className="font-serif text-2xl md:text-3xl text-lux-ink font-light tracking-tight mb-2.5">{l.head}</h3>
              <p className="text-[15px] text-lux-sec leading-relaxed font-light">{l.body}</p>
            </div>
          ))}
        </div>
      </Container>

      {/* CLOSING */}
      <section className="mt-24 relative min-h-[620px] flex items-center overflow-hidden">
        <BackgroundMedia videoUrl={closing.video_url} imageUrl={closingImg} testId="yu-closing-media" />
        <div className="absolute inset-0 bg-gradient-to-b from-lux-ink/60 to-lux-ink/95 pointer-events-none" />
        <Container className="relative">
          <p className="text-[11px] tracking-[0.35em] text-white/70 mb-4 font-medium">{closingEyebrow}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white font-light tracking-tight leading-snug">{closingHead}</h2>
          <p className="font-serif italic text-4xl md:text-5xl text-white font-light tracking-tight mt-7">{closingFinal}</p>
        </Container>
      </section>

      <div className="px-6 md:px-12 py-14 flex flex-col gap-2 bg-lux-bg">
        <Logo height={28} testId="yu-footer-logo" />
        <p className="text-[13px] text-lux-sec font-light mt-1">Completing what design begins.</p>
      </div>
      <WhatsAppFAB />
    </div>
  );
}
